/**
 * English copy (Capsico voice: Desi-Cool, plain about money and delays). Every other locale must
 * have exactly these keys — the type checker enforces it. Placeholders look like {name}.
 * Emojis are allowed in copy only, never as icons.
 */
export const en = {
  'common.retry': 'Try again',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.keep': 'Keep',
  'common.discard': 'Discard',
  'common.change': 'Change',
  'common.draftRestored': 'We kept what you typed last time.',
  'common.draftSaved': 'Draft saved',
  'common.wrong': 'Something went wrong. Try again.',
  'common.supportRef': 'Share this code with support',
  'common.copyRef': 'Copy code',
  'common.refCopied': 'Code copied',
  'common.offline': 'You are offline. Showing what we last loaded.',
  'common.unreachable': 'Capsico is not reachable',
  'common.unreachableBody': 'Check your internet connection.',

  'nav.home': 'Home',
  'nav.search': 'Search',
  'nav.account': 'Account',

  'location.deliverTo': 'Deliver to',
  'location.choose': 'Choose a delivery location',
  'location.useCurrent': 'Use my current location',
  'location.locating': 'Finding you…',
  'location.denied': 'Location is off. Turn it on, or pick your town below.',
  'location.unavailable': 'We could not get your location. Pick your town below.',
  'location.saved': 'Saved addresses',
  'location.towns': 'Or pick your town',
  'location.addAddress': 'Add a new address',
  'location.signInToSave': 'Sign in to save addresses',
  'location.current': 'Current location',
  'location.townCentre': '{town} (town centre)',
  'location.setFirst': 'Tell us where to deliver and we will show what is open near you.',
  'location.notServed': 'Not delivering here yet',

  'signin.title': 'Hungry? Sign in 🍛',
  'signin.subtitle': 'Enter your mobile number. We will send a 6-digit code.',
  'signin.phone': 'Mobile number',
  'signin.sendCode': 'Send code',
  'signin.codeTitle': 'Enter the code sent to {sentTo}',
  'signin.code': '6-digit code',
  'signin.verify': 'Verify',
  'signin.resendIn': 'Resend in {seconds}s',
  'signin.resend': 'Resend code',
  'signin.changeNumber': 'Change number',
  'signin.smsDelayed':
    'SMS is running late. If it does not arrive, ask Capsico support for the code.',
  'signin.terms': 'By continuing you agree to our Terms and Privacy Policy.',
  'signin.browse': 'Just browsing — skip for now',
  'signin.enterPhone': 'Enter your mobile number',
  'signin.enterCode': 'Enter the 6-digit code',
  'turnstile.label': 'Security check',
  'turnstile.required': 'Please complete the check above and try again.',
  'turnstile.failed': 'We could not confirm you are a person. Please try the check again.',

  'profile.title': 'A little about you',
  'profile.subtitle': 'So we can show the right food and greet you properly.',
  'profile.name': 'Your name',
  'profile.namePlaceholder': 'e.g. Asha Verma',
  'profile.diet': 'What do you eat?',
  'profile.veg': 'Veg only',
  'profile.nonVeg': 'Veg and non-veg',
  'profile.email': 'Email (optional)',
  'profile.emailPlaceholder': 'For bills, never for spam',
  'profile.birthday': 'Birthday (optional)',
  'profile.birthdayHint': 'A treat on your day 🎂',
  'profile.language': 'App language',
  'profile.save': 'Save and continue',
  'profile.saved': 'Profile saved',
  'profile.nameRequired': 'Tell us your name (at least 2 letters)',
  'profile.dietRequired': 'Pick veg or non-veg',
  'profile.edit': 'Edit profile',

  'address.title': 'Add address',
  'address.editTitle': 'Edit address',
  'address.pinHint': 'Move the map to put the pin on your gate',
  'address.house': 'House / flat number',
  'address.floor': 'Floor (optional)',
  'address.area': 'Street and area',
  'address.landmark': 'Nearby landmark (optional)',
  'address.landmarkPlaceholder': 'e.g. opposite the temple',
  'address.instructions': 'Note for the rider (optional)',
  'address.instructionsPlaceholder': 'e.g. ring the bell twice, dog is friendly',
  'address.saveAs': 'Save as',
  'address.home': 'Home',
  'address.work': 'Work',
  'address.other': 'Other',
  'address.otherName': 'Name this place',
  'address.otherPlaceholder': "e.g. Hostel, Mummy's place",
  'address.someoneElse': 'Ordering for someone else?',
  'address.receiverName': 'Their name',
  'address.receiverPhone': 'Their mobile number',
  'address.gatePhoto': 'Photo of the gate',
  'address.gatePhotoHint': 'Helps the rider find you first time',
  'address.addPhoto': 'Add photo',
  'address.replace': 'Replace',
  'address.remove': 'Remove',
  'address.voiceNote': 'Voice directions',
  'address.voiceHint': 'Up to 60 seconds — "turn left after the chai stall…"',
  'address.record': 'Record',
  'address.stop': 'Stop',
  'address.recording': 'Recording {time}',
  'address.uploading': 'Uploading {percent}%',
  'address.micDenied': 'Microphone is off. Allow it to record directions.',
  'address.makeDefault': 'Make this my default address',
  'address.save': 'Save address',
  'address.saved': 'Address saved',
  'address.deleted': 'Address removed',
  'address.deleteConfirm': 'Remove this address?',
  'address.default': 'Default',
  'address.setDefault': 'Make default',
  'address.notServed': 'We do not deliver here yet',
  'address.none': 'No saved addresses yet',
  'address.noneBody': 'Save home and work once — checkout gets faster.',

  'vote.title': 'Capsico is not here… yet 🛵',
  'vote.body': 'Every vote from this area moves it up our launch list.',
  'vote.cta': 'Bring Capsico here',
  'vote.voted': 'Vote counted! We will tell you when we arrive.',
  'vote.already': 'You have already voted for this area. Thank you!',
  'vote.nearby': '{count} people near you want Capsico',
  'vote.notify': 'Phone number to tell you when we launch (optional)',
  'vote.otherPlace': 'Try another location',

  'account.title': 'Account',
  'account.signedOutTitle': 'Your food, your way',
  'account.signedOutBody': 'Sign in to save addresses and get food delivered.',
  'account.signIn': 'Sign in',
  'account.addresses': 'Saved addresses',
  'account.language': 'Language',
  'account.theme': 'Appearance',
  'account.themeAuto': 'Auto (dark after 10 PM)',
  'account.themeLight': 'Light',
  'account.themeDark': 'Dark',
  'account.signOut': 'Sign out',
  'account.version': 'Capsico {version}',

  'home.searchPlaceholder': 'Search "biryani" or a restaurant',
  'home.promiseTitle1': 'Zero platform fee',
  'home.promiseBody1': 'You pay for the food and delivery. Nothing hidden.',
  'home.promiseTitle2': 'Menu price = app price',
  'home.promiseBody2': 'Same price as the counter. Checked every week.',
  'home.promiseTitle3': 'Your town, your kitchens',
  'home.promiseBody3': 'Local favourites, delivered by local riders.',
  'home.cuisines': 'Cuisines',
  'home.mind': "What's on your mind?",
  'home.top': 'Top restaurants near you',
  'home.emptyTitle': 'Kitchens are warming up 🔥',
  'home.emptyBody': 'No restaurant is taking orders here right now. Check back soon.',
  'home.closed': 'Closed',
  'home.newBadge': 'New',
  'home.legend': 'Local Legend',
  'home.forTwo': '{amount} for two',
  'home.places': '{count} places',

  'search.placeholder': 'Search dishes or restaurants',
  'search.sort': 'Sort',
  'search.filterVeg': 'Veg',
  'search.filterPureVeg': 'Pure veg',
  'search.filterRating': 'Rating 4+',
  'search.filterFast': 'Fast delivery',
  'search.filterNew': 'New',
  'search.filterOpen': 'Open now',
  'search.results': '{count} restaurants',
  'search.noneTitle': 'Nothing matched',
  'search.noneBody': 'Try another dish name, or clear the filters.',
  'search.clear': 'Clear filters',
  'search.matched': 'Has {dishes}',
  'search.start': 'Search for a dish, cuisine or restaurant',

  'restaurant.licence': '{kind} licence {number}',
  'restaurant.vegOnly': 'Veg only',
  'restaurant.searchMenu': 'Search in menu',
  'restaurant.bestseller': 'Bestseller',
  'restaurant.customisable': 'Customisable',
  'restaurant.add': 'ADD',
  'restaurant.menuEmpty': 'This menu is being updated. Check back soon.',
  'restaurant.noMatch': 'No dish matches "{query}"',
  'restaurant.closed': 'Closed right now',
  'restaurant.notFound': 'This restaurant is not available',
  'restaurant.items': '{count} items',

  'customise.size': 'Choose a size',
  'customise.required': 'Required',
  'customise.optional': 'Optional',
  'customise.upTo': 'Choose up to {max}',
  'customise.exactly': 'Choose {count}',
  'customise.between': 'Choose {min} to {max}',
  'customise.pick': 'Pick {count}',
  'customise.total': 'Item total',
  'customise.addToCart': 'Add item · {price}',
  'customise.cartSoon': 'Ordering opens very soon — your cart arrives in the next update 🙌',
  'customise.incomplete': 'Finish the required choices',
  'cart.items': '{count} items',
  'cart.item': '1 item',
  'cart.view': 'View cart',
  'cart.added': 'Added to cart 🛒',
  'cart.full': 'Your cart is full — up to 30 different dishes',
  'cart.replaceTitle': 'Start a new cart?',
  'cart.replaceBody':
    'Your cart has dishes from {name}. Start a new cart with this restaurant instead?',
  'cart.replaceYes': 'Start new cart',
  'cart.replaceNo': 'Keep old cart',
  'cart.empty': 'Your cart is empty',
  'cart.emptyHint': 'Pick something tasty from a restaurant near you 😋',
  'cart.browse': 'Find food',
  'checkout.title': 'Checkout',
  'checkout.addMore': 'Add more items',
  'checkout.kitchenNote': 'Note for the restaurant',
  'checkout.kitchenNoteHint': 'e.g. Less spicy, no onion',
  'checkout.cutlery': 'Send cutlery',
  'checkout.cutleryHint': 'Off by default — good for the planet 🌱',
  'checkout.offers': 'Offers & coupons',
  'checkout.viewOffers': 'View all offers',
  'checkout.applied': '{code} applied',
  'checkout.youSave': 'You save {amount}',
  'checkout.remove': 'Remove',
  'checkout.apply': 'Apply',
  'checkout.enterCode': 'Enter coupon code',
  'checkout.funding.restaurant': 'Offer by Restaurant',
  'checkout.funding.capsico': 'Offer by Capsico',
  'checkout.funding.shared': 'Offer by Restaurant + Capsico',
  'checkout.bill': 'Bill details',
  'checkout.toPay': 'To pay',
  'checkout.savedTotal': 'You saved {amount} on this order 🎉',
  'checkout.why': 'Why this fee?',
  'checkout.gotIt': 'Got it',
  'checkout.tip': 'Tip your rider',
  'checkout.tipHint': '100% of the tip goes to your rider',
  'checkout.noTip': 'No tip',
  'checkout.deliverTo': 'Deliver to',
  'checkout.change': 'Change',
  'checkout.pickAddress': 'Choose a delivery address',
  'checkout.addAddress': 'Add new address',
  'checkout.needAddress': 'Add an address to place your order',
  'checkout.someoneElse': 'Ordering for someone else?',
  'checkout.receiverName': "Receiver's name",
  'checkout.receiverPhone': "Receiver's phone",
  'checkout.phoneError': 'Enter a valid phone number',
  'checkout.when': 'When',
  'checkout.now': 'Now',
  'checkout.prepaidOnly': 'Scheduled orders must be paid online',
  'checkout.payment': 'Pay with',
  'checkout.cod': 'Cash on delivery',
  'checkout.test': 'Test payment (development only)',
  'checkout.place': 'Place order',
  'checkout.placing': 'Placing your order…',
  'checkout.signIn': 'Sign in to place your order',
  'checkout.pricesChanged': 'Prices changed — check the bill and place again',
  'checkout.placed': 'Order placed! 🎉',
  'checkout.freeDelivery': 'Free delivery on this order 🎉',
  'checkout.freeLeft': '{count} more free-delivery orders after this',
  'checkout.minimum': 'Add {amount} more to place this order',
  'checkout.unavailable': "Couldn't load your bill",
  'orders.title': 'Your orders',
  'orders.empty': 'No orders yet',
  'orders.emptyHint': 'Your first order is one tap away 🍛',
  'orders.placedTitle': 'Order placed! 🎉',
  'orders.placedBody': '{name} has your order',
  'orders.number': 'Order #{number}',
  'orders.eta': 'Arriving in {eta}',
  'orders.scheduledFor': 'Scheduled for {time}',
  'orders.trackSoon': 'Live rider tracking arrives in the next update',
  'orders.backHome': 'Back to home',
  'orders.payCash': 'Pay {amount} in cash at the door',
  'orders.paid': 'Paid',
  'orders.refunded': 'Refunded',
  'orders.items': 'Items',
  'orders.deliveringTo': 'Delivering to',
  'orders.cancelledBecause': 'Cancelled: {reason}',
  'orders.notFound': 'Order not found',
  'stage.scheduled': 'Scheduled',
  'stage.placed': 'Waiting for the restaurant',
  'stage.preparing': 'Being prepared 🍳',
  'stage.ready': 'Ready for pickup',
  'stage.on_the_way': 'On the way 🛵',
  'stage.delivered': 'Delivered',
  'stage.cancelled': 'Cancelled',
  'account.orders': 'Your orders',
  'common.today': 'Today',
  'common.tomorrow': 'Tomorrow',
  'stage.awaiting_payment': 'Waiting for payment',
  'account.wallet': 'Capsico Money',
  'pay.wallet': 'Capsico Money',
  'pay.walletUsing': '{amount} will be used on this order',
  'pay.walletOff': 'Turn on to use your balance',
  'pay.walletExpires': '{amount} expires on {date}',
  'pay.walletApplied': '{amount} Capsico Money applied',
  'pay.payAmount': 'Pay {amount}',
  'pay.payWallet': 'Pay with Capsico Money',
  'pay.secure': '100% secure payments',
  'pay.method.cod': 'Cash on delivery',
  'pay.method.test': 'Test payment (development only)',
  'pay.method.wallet': 'Capsico Money',
  'pay.method.upi': 'UPI',
  'pay.method.card': 'Credit / debit card',
  'pay.method.netbanking': 'Netbanking',
  'pay.method.esewa': 'eSewa',
  'pay.method.khalti': 'Khalti',
  'pay.method.fonepay': 'Fonepay QR',
  'pay.method.imepay': 'IME Pay',
  'pay.method.connectips': 'ConnectIPS',
  'payment.paid': 'Payment received 🎉',
  'payment.notFinished': 'Payment not finished. You can pay again from here.',
  'payment.notYet': 'We have not received it yet — give it a few seconds.',
  'payment.failed': 'Payment did not go through',
  'payment.cancelled': 'Order cancelled',
  'payment.qrCopied': 'Code copied',
  'payment.gatewayDown': 'Could not open the payment page. Check your internet and try again.',
  'payment.waitingTitle': 'Finish your payment',
  'payment.waitingBody':
    'Pay {amount} — the restaurant starts cooking as soon as the money comes in.',
  'payment.payNow': 'Pay now',
  'payment.payAnother': 'Pay another way',
  'payment.cancelOrder': 'Cancel order',
  'payment.keepOrder': 'Keep order',
  'payment.cancelTitle': 'Cancel this order?',
  'payment.cancelBody':
    'If any money was taken, it comes back to you. Capsico Money used on this order goes back to your balance.',
  'payment.pickMethod': 'Choose how to pay',
  'payment.qrTitle': 'Scan to pay {amount}',
  'payment.qrBody':
    'Open your bank or wallet app, scan or paste this Fonepay code and pay. We will spot the payment by ourselves.',
  'payment.qrCopy': 'Copy code',
  'payment.qrWaiting': 'Waiting for your payment…',
  'payment.qrPaid': "I've paid",
  'payment.partlyRefunded': '{amount} refunded',
  'payment.failedStatus': 'Payment did not go through',
  'payment.walletUsed': '{amount} paid with Capsico Money',
  'payment.returnTitle': 'Payment',
  'payment.confirming': 'Confirming your payment…',
  'payment.confirmingBody': "Hold on — we are checking with {gateway}. Please don't close the app.",
  'payment.slow': 'This is taking longer than usual. Your order page will update by itself.',
  'payment.viewOrder': 'View order',
  'payment.failedBody':
    'If money left your account, it comes back automatically. Try again or pick another way.',
  'payment.tryAgain': 'Try again',
  'payment.returnBroken': 'We could not read this payment',
  'payment.returnBrokenBody': 'Open the order from Your orders to see where it stands.',
  'refund.title': 'Refunds',
  'refund.toWallet': 'To Capsico Money',
  'refund.toSource': 'To your original payment method',
  'refund.status.awaiting_choice': 'Pick where it goes',
  'refund.status.processing': 'Processing · 3–5 days',
  'refund.status.manual': 'Processing · 3–5 days',
  'refund.status.done': 'Refunded',
  'refund.status.failed': 'Delayed — support will reach you',
  'refund.choiceTitle': 'Where should {amount} go?',
  'refund.choiceWallet': 'Capsico Money now',
  'refund.choiceWalletBonus': 'Capsico Money now (+{amount} bonus)',
  'refund.choiceWalletHint': 'Instant — use it on your next order',
  'refund.choiceSource': 'Back to original method',
  'refund.choiceSourceHint': 'Takes 3–5 days',
  'refund.chooseBy': 'Pick by {time}, else it goes to Capsico Money',
  'refund.saved': 'Done — your refund is on its way',
  'refund.reason.restaurant_rejected': 'Restaurant could not take the order',
  'refund.reason.not_accepted': 'Restaurant did not accept in time',
  'refund.reason.ops_cancelled': 'Cancelled by Capsico',
  'refund.reason.customer_cancelled': 'You cancelled the order',
  'refund.reason.payment_timeout': 'Payment did not finish in time',
  'refund.reason.complaint': 'Complaint approved',
  'refund.reason.goodwill': 'Goodwill credit',
  'invoice.title': 'Tax invoices',
  'invoice.food': 'Food invoice',
  'invoice.fees': 'Capsico fees invoice',
  'invoice.download': 'Download',
  'invoice.failed': 'Could not download the invoice. Try again.',
  'wallet.title': 'Capsico Money',
  'wallet.balance': 'Capsico Money balance',
  'wallet.heroHint': 'Use it on any order — it applies at checkout',
  'wallet.storeCredit': 'Store credit only — no top-ups. Every credit has an expiry date.',
  'wallet.credits': 'Your credits',
  'wallet.expiresOn': 'Expires {date}',
  'wallet.left': '{left} left of {total}',
  'wallet.source.refund': 'Refund',
  'wallet.source.complaint_bonus': 'Bonus for choosing Capsico Money',
  'wallet.source.goodwill': 'From Capsico',
  'wallet.source.compensation': 'Sorry credit',
  'wallet.source.referral': 'Refer a friend reward',
  'wallet.source.cashback': 'Cashback',
  'wallet.source.winback': 'Welcome back credit',
  'wallet.history': 'History',
  'wallet.entry.credit': 'Added',
  'wallet.entry.debit': 'Used on an order',
  'wallet.entry.reverse': 'Returned',
  'wallet.entry.expire': 'Expired',
  'wallet.empty': 'No Capsico Money yet',
  'wallet.emptyHint': 'Refunds and credits from Capsico show up here.',
  'wallet.pending': 'Refunds waiting for your choice',
  'wallet.noHistory': 'Nothing here yet',
  'wallet.country.IN': 'India ₹',
  'wallet.country.NP': 'Nepal Rs',
  'checkout.leaveAtDoor': 'Leave at the door',
  'checkout.leaveAtDoorHint': 'No knock, no code — the rider leaves it and sends you a photo 📸',
  'track.stage.placed': 'Order placed',
  'track.stage.accepted': 'Restaurant accepted',
  'track.stage.preparing': 'Preparing your food 🍳',
  'track.stage.ready': 'Food is ready',
  'track.stage.rider_assigned': 'Rider is on the way to the restaurant',
  'track.stage.picked_up': 'Picked up — on the way 🛵',
  'track.stage.nearby': 'Rider is nearby',
  'track.stage.delivered': 'Delivered 🎉',
  'track.stage.cancelled': 'Order cancelled',
  'track.step.placed': 'Order placed',
  'track.step.accepted': 'Accepted',
  'track.step.preparing': 'Preparing',
  'track.step.ready': 'Ready',
  'track.step.rider_assigned': 'Rider assigned',
  'track.step.picked_up': 'Picked up',
  'track.step.nearby': 'Nearby',
  'track.step.delivered': 'Delivered',
  'track.step.cancelled': 'Cancelled',
  'track.sub.placed': 'Waiting for {name} to accept',
  'track.sub.accepted': '{name} has your order',
  'track.sub.preparing': '{name} is cooking it fresh',
  'track.sub.ready': 'Packed and waiting for your rider',
  'track.sub.findingRider': 'Finding a rider near the restaurant…',
  'track.sub.rider_assigned': '{rider} is heading to the restaurant',
  'track.sub.picked_up': '{rider} is on the way to you',
  'track.sub.nearby': '{rider} is almost at your door — keep your phone handy',
  'track.sub.delivered': 'Enjoy your meal!',
  'track.etaArriving': 'Arriving in',
  'track.etaRange': '{from}–{to} min',
  'track.etaMinutes': '{minutes} min',
  'track.late': 'Running late',
  'track.lateBody': 'Sorry, this is taking longer than we promised. We are on it.',
  'track.lateApology': "Sorry your order was late 🙏 We've added Capsico Money to your wallet.",
  'track.reconnecting': 'Live updates paused. Refreshing every 15 seconds…',
  'track.mapLabel': 'Map of your delivery',
  'track.pinRestaurant': 'Restaurant',
  'track.pinHome': 'Your location',
  'track.pinRider': 'Your rider',
  'track.recenter': 'Show everything on the map',
  'track.steps': 'Order progress',
  'track.rider.call': 'Call rider',
  'track.rider.chat': 'Chat with rider',
  'track.rider.unread': '{count} new messages',
  'track.rider.masked': 'Calls go through Capsico — your number stays private.',
  'track.vehicle.walker': 'On foot',
  'track.vehicle.cyclist': 'Cycle',
  'track.vehicle.motorbike': 'Bike',
  'track.callUnavailable': 'Calling is not available right now. Use chat or call Capsico support.',
  'track.code.title': 'Delivery code',
  'track.code.hint': 'Share this code with the rider only at your door.',
  'track.code.locked':
    'Too many wrong tries. Capsico support will help the rider finish this delivery.',
  'track.code.failed': "Couldn't load your code.",
  'track.leaveAtDoor': 'Leave at the door — your rider will send a photo once it is there.',
  'track.proof.title': 'Left at your door',
  'track.proof.caption': 'Photo taken by your rider. Kept for 30 days.',
  'chat.title': 'Chat with {name}',
  'chat.placeholder': 'Type a message',
  'chat.send': 'Send',
  'chat.sending': 'Sending…',
  'chat.closed': 'Chat closes once your order is delivered.',
  'chat.empty': 'Say hi or tap a quick reply 👋',
  'chat.failed': "Your message didn't go. Try again.",
  'chat.quick.at_gate': "I'm at the gate",
  'chat.quick.please_call': 'Please call me',
  'chat.quick.coming_down': 'Coming down',
  'chat.quick.wait_two': 'Please wait 2 minutes',
  'chat.quick.on_the_way': 'On my way',
  'chat.quick.reached': 'I have reached',

  // W16 Help + chat
  'help.title': 'Help & support',
  'help.orderHelp': 'Help with this order',
  'help.myTickets': 'Your chats',
  'help.noTickets': 'No help chats yet',
  'help.noTicketsBody': 'Something wrong? Start a chat — we usually sort it in minutes.',
  'help.recentOrders': 'Help with a recent order',
  'help.otherTopics': 'Something else',
  'help.otherBody': 'Account, payments or anything not about one order',
  'help.deskOpen': 'Our team is online',
  'help.deskClosed': 'Our team is offline right now',
  'help.botNote': 'Our assistant answers instantly, even at night. {hours}',
  'help.loadFailed': 'Help could not load',
  'help.newTitle': 'What went wrong?',
  'help.newTitleOrder': 'Help with order {number}',
  'help.reasonLabel': 'Pick a reason',
  'help.openExisting': 'You already have a chat about this',
  'help.openChat': 'Open chat',
  'help.items': 'Which items?',
  'help.itemsHint': 'Pick what was missing or wrong',
  'help.itemOf': '{picked} of {ordered}',
  'help.less': 'One less',
  'help.more': 'One more',
  'help.photos': 'Add photos',
  'help.photosHint': 'A clear photo helps us fix it faster (up to {max})',
  'help.addPhoto': 'Add photo',
  'help.removePhoto': 'Remove photo',
  'help.photoFailed': 'Failed',
  'help.photoType': 'Use a JPG, PNG or WebP photo',
  'help.photoSize': 'That photo is too big (max 8 MB)',
  'help.photoMany': 'You can add up to {max} photos',
  'help.photoUpload': "Photo didn't upload. Try again.",
  'help.note': 'Anything else? (optional)',
  'help.notePlaceholder': 'Tell us what happened',
  'help.needItems': 'Pick at least one item',
  'help.needPhoto': 'Add a photo to continue',
  'help.uploading': 'Wait for the photos to upload',
  'help.submit': 'Start chat',
  'help.createFailed': "We couldn't open the chat. Try again.",
  'help.group.order': 'Food & items',
  'help.group.delivery': 'Delivery',
  'help.group.payment': 'Payments & refunds',
  'help.group.conduct': 'Behaviour',
  'help.group.partner': 'Restaurant partners',
  'help.group.rider': 'Riders',
  'help.group.other': 'Other',
  'help.status.bot': 'With the assistant',
  'help.status.queued': 'Waiting for an agent',
  'help.status.assigned': 'Agent replying',
  'help.status.waiting_customer': 'Waiting for you',
  'help.status.resolved': 'Resolved',
  'help.status.closed': 'Closed',
  'help.chatTitle': 'Chat {number}',
  'help.orderLabel': 'Order {number}',
  'help.bot': 'Capsico assistant',
  'help.agent': 'Capsico support',
  'help.team': 'Capsico',
  'help.empty': 'Tell us what happened and we will take it from here.',
  'help.placeholder': 'Type a message',
  'help.send': 'Send',
  'help.attach': 'Add photo',
  'help.sending': 'Sending…',
  'help.sendFailed': "Your message didn't go. Try again.",
  'help.actionFailed': "That didn't work. Try again.",
  'help.talkToAgent': 'Talk to an agent',
  'help.agentAsked': 'Done — an agent will join soon',
  'help.queue': 'You are #{n} in the queue',
  'help.agentReplying': '{name} from Capsico is helping you',
  'help.language': 'Chat in',
  'help.reconnecting': 'Reconnecting… updating every 15 s',
  'help.closedNotice': 'This chat is closed. Start a new one if you still need help.',
  'help.refunds': 'Refunds on this chat',
  'help.rateTitle': 'How did we do?',
  'help.rateBody': 'Rate this chat — it helps us get better.',
  'help.star': '{n} out of 5',
  'help.rateComment': 'Anything to add? (optional)',
  'help.rateSubmit': 'Submit rating',
  'help.rated': 'Thanks for rating!',
  'help.ratedLine': 'You rated this chat {score} / 5',
  'help.notFound': 'Chat not found',
  'help.callAsked': 'Call requested — we will ring you soon',
  'help.requestCall': 'Request a call',
  'help.callRequested': 'Call requested at {time}',
  'help.unread': 'New reply',
  'rate.title': 'Rate your order',
  'rate.from': 'from {name}',
  'rate.editTitle': 'Edit your rating',
  'rate.food': 'How was the food?',
  'rate.delivery': 'How was the delivery?',
  'rate.star1': 'Terrible',
  'rate.star2': 'Bad',
  'rate.star3': 'Okay',
  'rate.star4': 'Good',
  'rate.star5': 'Loved it',
  'rate.starAria': '{n} out of 5',
  'rate.dishes': 'Which dishes did you like?',
  'rate.like': 'Liked {name}',
  'rate.unlike': 'Did not like {name}',
  'rate.text': 'Tell others about it (optional)',
  'rate.textPlaceholder': 'What did you love? What could be better?',
  'rate.count': '{n}/{max}',
  'rate.photos': 'Add photos',
  'rate.photosHint': 'Up to 3 photos of your food',
  'rate.addPhoto': 'Add photo',
  'rate.removePhoto': 'Remove photo',
  'rate.uploading': 'Uploading {percent}%',
  'rate.photoFailed': 'Upload failed',
  'rate.submit': 'Submit rating',
  'rate.save': 'Save changes',
  'rate.editUntil': 'You can edit this until {time}',
  'rate.thanks': 'Thanks for rating! 🙌',
  'rate.updated': 'Rating updated',
  'rate.changed': 'This rating changed somewhere else. We reloaded it.',
  'rate.failed': "Your rating didn't go. Try again.",
  'rate.badType': 'Use a JPG, PNG or WebP photo',
  'rate.tooBig': 'That photo is too big (8 MB max)',
  'rate.tooMany': 'You can add up to 3 photos',
  'rate.uploadFailed': "A photo didn't upload. Remove it and try again.",
  'rate.blockTitle': "We can't take this rating",
  'rate.block.not_delivered': 'You can rate this order once it is delivered.',
  'rate.block.window_closed': 'The time to rate this order has passed.',
  'rate.block.already_reviewed':
    'You have already rated this order — you can edit your rating from My reviews.',
  'rate.block.self_review': "Ratings from the restaurant's own team or phones aren't allowed.",
  'rate.block.self_order': "This order can't be rated.",
  'rate.block.not_yours': 'This order belongs to another account.',
  'rate.cardTitle': 'How was your order?',
  'rate.cardBody': 'Rate {name} — it takes 10 seconds',
  'rate.cardAction': 'Rate now',
  'rate.rateBy': 'Rate by {time}',
  'rate.yours': 'Your rating',
  'rate.foodShort': 'Food',
  'rate.deliveryShort': 'Delivery',
  'rate.stripTitle': 'Rate your orders',
  'rate.stripOne': 'How was {name}?',
  'rate.stripMany': '{name} and {count} more',
  'rate.stripAction': 'Rate',
  'reviews.title': 'Ratings & reviews',
  'reviews.new': 'New',
  'reviews.newBody': 'Not enough ratings yet — be one of the first',
  'reviews.count': '{count} ratings · last 90 days',
  'reviews.seeAll': 'See all reviews',
  'reviews.empty': 'No reviews yet',
  'reviews.emptyBody': 'Order and be the first to review 🍛',
  'reviews.sort': 'Sort reviews',
  'reviews.sort.recent': 'Recent',
  'reviews.sort.highest': 'Highest',
  'reviews.sort.lowest': 'Lowest',
  'reviews.sort.photos': 'With photos',
  'reviews.reply': 'Reply from the restaurant',
  'reviews.liked': 'Liked: {dishes}',
  'reviews.hygiene.green': 'Hygiene: very good',
  'reviews.hygiene.amber': 'Hygiene: good',
  'reviews.loadMore': 'Load more',
  'reviews.photo': 'Review photo',
  'reviews.stars': '{n} stars',
  'reviews.end': "That's all the reviews",
  'my.title': 'My reviews',
  'my.toRate': 'Waiting for your rating',
  'my.yours': 'Your reviews',
  'my.empty': 'No reviews yet',
  'my.emptyBody': 'Your ratings show up here after you rate a delivered order.',
  'my.status.held': 'Under review — not shown yet',
  'my.status.hidden': 'Text hidden by Capsico — your stars still count',
  'my.status.removed': 'Removed from the rating',
  'my.order': 'Order #{number}',
  'account.reviews': 'My reviews',
  'restaurant.likes': '{count} liked',
  /* W18: notifications + growth */
  'nav.inbox': 'Notifications',
  'inbox.title': 'Notifications',
  'inbox.bell': 'Notifications',
  'inbox.bellUnread': 'Notifications, {count} new',
  'inbox.unreadCount': '{count} new',
  'inbox.readAll': 'Mark all read',
  'inbox.empty': 'No notifications yet',
  'inbox.emptyBody': 'Order updates, offers and replies from support land here.',
  'inbox.loadMore': 'Load more',
  'inbox.end': "You're all caught up ✨",
  'inbox.settings': 'Notification settings',
  'inbox.signIn': 'Sign in to see your notifications',
  'inbox.failed': 'Could not update. Try again.',
  'inbox.new': 'New',
  'notify.cat.orders': 'Orders',
  'notify.cat.support': 'Help & support',
  'notify.cat.account': 'Account & money',
  'notify.cat.offers': 'Offers & deals',
  'notify.cat.reminders': 'Reminders',
  'prefs.title': 'Notification settings',
  'prefs.categories': 'What we tell you about',
  'prefs.ordersHint': 'Order updates, delivery codes, refunds and support replies',
  'prefs.ordersLocked': 'Always on — you need these to get your food',
  'prefs.offersHint': 'Deals, new restaurants and offer launches',
  'prefs.remindersHint': 'Cart left behind, rate your order',
  'prefs.channels': 'Where we send offers and reminders',
  'prefs.channelsHint': 'Order, support and account messages always reach you.',
  'prefs.push': 'Push notifications',
  'prefs.sms': 'SMS',
  'prefs.whatsapp': 'WhatsApp',
  'prefs.saved': 'Notification settings saved',
  'prefs.failed': 'Could not save. Try again.',
  'prefs.quiet': 'We never send offers between 10 PM and 11 AM, and at most 3 a week.',
  'account.notifications': 'Notifications',
  'account.refer': 'Refer & earn',
  'account.campus': 'Campus Pass',
  'refer.title': 'Refer & earn',
  'refer.hero': 'Invite friends, earn {amount} each 🎉',
  'refer.heroBody':
    'Your friend gets {offer} on their first order. You get {amount} Capsico Money when it is delivered.',
  'refer.heroBodyNoOffer':
    "You get {amount} Capsico Money when your friend's first order is delivered.",
  'refer.yourCode': 'Your code',
  'refer.copy': 'Copy',
  'refer.copied': 'Code copied',
  'refer.linkCopied': 'Invite copied — paste it anywhere',
  'refer.share': 'Share invite',
  'refer.shareTitle': 'Order on Capsico',
  'refer.friendsJoined': 'Friends joined',
  'refer.waiting': 'Waiting',
  'refer.earned': 'Earned',
  'refer.monthCap': '{used} of {cap} rewards used this month',
  'refer.history': 'Your invites',
  'refer.noHistory': 'No invites yet. Share your code to get started.',
  'refer.status.pending': 'Waiting for first delivery',
  'refer.status.rewarded': 'Rewarded',
  'refer.status.blocked': 'Not rewarded',
  'refer.status.expired': 'Expired',
  'refer.rules': 'How it works',
  'refer.rule1': 'Share your code or link with friends who have never ordered on Capsico.',
  'refer.rule2': 'Your friend gets the welcome offer on their first order.',
  'refer.rule3':
    'You get {amount} Capsico Money once their first order is delivered. It expires in {days} days.',
  'refer.rule4':
    'Up to {cap} rewards a month. The same phone, address or payment account as yours does not count.',
  'refer.haveCode': "Got a friend's code?",
  'refer.codeLabel': 'Friend, ambassador or creator code',
  'refer.apply': 'Apply',
  'growth.codeApplied': 'Code {code} applied 🎉',
  'growth.offerReady': 'Offer {code} is ready at checkout 🎁',
  'campus.title': 'Campus Pass',
  'campus.hero': 'Free delivery on campus 🎓',
  'campus.heroBody': 'Free delivery within {km} km of your campus for {days} days. Students only.',
  'campus.pick': 'Choose your campus',
  'campus.distance': '{km} km away',
  'campus.freeWithin': 'Free delivery within {km} km',
  'campus.noCampus': 'No campus near you yet',
  'campus.noCampusBody': "Campus Pass is live at a few colleges. We're adding more.",
  'campus.useLocation': 'Find campuses near me',
  'campus.locating': 'Finding your location…',
  'campus.how': 'How do you want to verify?',
  'campus.methodEmail': 'College email',
  'campus.methodEmailHint': 'We send a 6-digit code to your college email',
  'campus.methodPhoto': 'Student ID photo',
  'campus.methodPhotoHint': 'Our team checks it, usually within a day',
  'campus.email': 'College email',
  'campus.emailHint': 'Must end with {domains}',
  'campus.emailBad': 'Use your college email address',
  'campus.ambassador': 'Ambassador code (optional)',
  'campus.sendCode': 'Send code',
  'campus.continue': 'Continue',
  'campus.sent': 'Code sent to your college email 📧',
  'campus.codeTitle': 'Check your college email',
  'campus.codeBody': 'Enter the 6-digit code we sent to {email}.',
  'campus.code': '6-digit code',
  'campus.verify': 'Verify',
  'campus.verified': 'Campus Pass is active 🎉',
  'campus.startOver': 'Start again',
  'campus.photoTitle': 'Upload your student ID',
  'campus.photoBody': 'A clear photo of the front, with your name and college visible.',
  'campus.photoPick': 'Add photo',
  'campus.photoReplace': 'Replace',
  'campus.photoRemove': 'Remove',
  'campus.photoUpload': 'Send for review',
  'campus.photoUploading': 'Uploading {percent}%',
  'campus.photoTooBig': 'Photo must be a JPG, PNG or WebP under 8 MB',
  'campus.uploaded': "ID sent — we'll check it soon",
  'campus.failed': 'Something went wrong. Try again.',
  'campus.status.pending_email': 'Check your college email',
  'campus.status.pending_photo': 'Upload your ID card',
  'campus.status.pending_review': 'Being checked',
  'campus.status.active': 'Active',
  'campus.status.rejected': 'Not approved',
  'campus.status.expired': 'Expired',
  'campus.activeBody': 'Free delivery within {km} km of {campus}.',
  'campus.validUntil': 'Valid until {date}',
  'campus.reviewBody': "We're checking your ID for {campus}. We'll notify you.",
  'campus.rejectedBody': 'Reason: {reason}',
  'campus.tryAgain': 'Apply again',
  'campus.expiredBody': 'Your pass for {campus} has ended. Apply again to renew.',
  'cashback.orderEarned': 'You earned {amount} cashback on this order 🎉',
  'cashback.expires': 'Added as Capsico Money · expires {date}',
  'cashback.hint': '{percent}% cashback on this order 🎉',
  'cashback.hintBody': 'About {amount} back as Capsico Money after delivery.',
  'cashback.walletTitle': 'Cashback earned',
  'cashback.walletTotal': '{amount} so far at {percent}% on eligible orders',
  'cashback.order': 'Order #{number}',
  'cashback.orderNoNumber': 'Order',
  'winback.title': 'Welcome back! 🎁',
  'winback.body': '{amount} Capsico Money is waiting — use it before {date}.',
  'deeplink.opening': 'Opening…',
  'legal.title': 'Legal',
  'legal.terms': 'Terms of use',
  'legal.privacy': 'Privacy policy',
  'legal.refunds': 'Refunds and cancellations',
  'legal.cookies': 'Cookie notice',
  'legal.restaurantTerms': 'Restaurant partner terms',
  'legal.riderTerms': 'Delivery partner terms',
  'legal.grievance': 'Grievance officer',
  'legal.openSite': 'Open on capsicoeats.com',
  'legal.loadFailed': 'We could not load this document',
  'legal.loadFailedBody': 'Check your connection, or read it on our website.',
  'legal.version': 'Version {version}',
  'legal.reconsentTitle': 'We have updated our terms 📄',
  'legal.reconsentBody': 'Please read what changed and accept to keep using Capsico.',
  'legal.whatChanged': 'What changed',
  'legal.read': 'Read the full document',
  'legal.hide': 'Hide the document',
  'legal.agree': 'I have read and agree to the updated documents',
  'legal.accept': 'Accept and continue',
  'legal.acceptFailed': 'Could not save your acceptance. Try again.',
  'legal.signOut': 'Sign out instead',
  'update.requiredTitle': 'Please update the app',
  'update.requiredBody': 'This version is no longer supported. Update to keep using Capsico.',
  'update.availableTitle': 'A new version is ready',
  'update.availableBody': 'Update to get the latest fixes and features.',
  'update.version': 'Version {version}',
  'update.notes': "What's new",
  'update.now': 'Update now',
  'update.update': 'Update',
  'update.reload': 'Reload',
  'update.later': 'Later',
  'integrity.title': 'Install Capsico from the Play Store',
  'integrity.body':
    'We could not verify this copy of the app. Install the official Capsico app from Google Play to order and pay.',
  'integrity.openStore': 'Open Play Store',
  'integrity.retry': 'Try again',
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
